import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CookieIcon } from '@phosphor-icons/react';
import { Switch } from '@/components/ui/switch';
import { getConsent, saveConsent } from '@/lib/consent';

const btnClass = 'px-5 py-2.5 rounded-full text-sm font-semibold transition text-center';

const CategoryRow = ({ label, desc, checked, onChange, disabled, testId }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-white/10 last:border-0">
    <div>
      <p className="text-sm font-semibold text-white">{label}</p>
      <p className="text-xs text-white/50 mt-0.5">{desc}</p>
    </div>
    <Switch
      data-testid={testId}
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      className="data-[state=unchecked]:bg-white/25 data-[state=checked]:bg-[#2563EB] border-white/20"
    />
  </div>
);

export default function ConsentBanner() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
    const open = () => {
      const c = getConsent();
      setAnalytics(!!c?.categories?.analytics);
      setMarketing(!!c?.categories?.marketing);
      setCustomizing(true);
      setVisible(true);
    };
    window.addEventListener('fh:open-consent', open);
    return () => window.removeEventListener('fh:open-consent', open);
  }, []);

  if (!visible) return null;

  const decide = (cats, choice) => {
    saveConsent(cats, { choice, lang: (i18n.resolvedLanguage || i18n.language || '').slice(0, 2) });
    setVisible(false);
    setCustomizing(false);
  };

  return (
    <div data-testid="consent-banner" className="fixed inset-x-0 bottom-0 z-[9998] p-3 sm:p-4 pointer-events-none">
      <div className="max-w-3xl mx-auto rounded-2xl border border-white/15 bg-[#0B0F19]/95 backdrop-blur-xl shadow-2xl text-white p-4 sm:p-5 pointer-events-auto">
        <div className="flex items-start gap-3">
          <CookieIcon size={22} weight="duotone" className="text-[#3B82F6] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold font-[Outfit]">{t('consent.title')}</p>
            <p className="text-xs text-white/60 mt-1 leading-relaxed">
              {t('consent.desc')}{' '}
              <Link to="/privacy" data-testid="consent-privacy-link" className="underline hover:text-white">{t('consent.privacy_policy')}</Link>
              {' · '}
              <Link to="/cookies" data-testid="consent-cookies-link" className="underline hover:text-white">{t('consent.cookie_policy')}</Link>
            </p>
          </div>
        </div>

        {customizing && (
          <div data-testid="consent-customize-panel" className="mt-3 rounded-xl bg-white/5 border border-white/10 px-4 py-1">
            <CategoryRow
              label={t('consent.essential')}
              desc={t('consent.essential_desc')}
              checked
              disabled
              testId="consent-essential-switch"
            />
            <CategoryRow
              label={t('consent.analytics')}
              desc={t('consent.analytics_desc')}
              checked={analytics}
              onChange={setAnalytics}
              testId="consent-analytics-switch"
            />
            <CategoryRow
              label={t('consent.marketing')}
              desc={t('consent.marketing_desc')}
              checked={marketing}
              onChange={setMarketing}
              testId="consent-marketing-switch"
            />
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
          <button
            data-testid="consent-accept-all-btn"
            onClick={() => decide({ analytics: true, marketing: true }, 'accept_all')}
            className={`${btnClass} bg-white text-black hover:bg-white/90 flex-1 sm:flex-none`}
          >
            {t('consent.accept_all')}
          </button>
          <button
            data-testid="consent-reject-btn"
            onClick={() => decide({ analytics: false, marketing: false }, 'reject_all')}
            className={`${btnClass} bg-white text-black hover:bg-white/90 flex-1 sm:flex-none`}
          >
            {t('consent.reject')}
          </button>
          {customizing ? (
            <button
              data-testid="consent-save-prefs-btn"
              onClick={() => decide({ analytics, marketing }, 'customize')}
              className={`${btnClass} bg-[#2563EB] text-white hover:bg-[#1D4ED8] flex-1 sm:flex-none`}
            >
              {t('consent.save')}
            </button>
          ) : (
            <button
              data-testid="consent-customize-btn"
              onClick={() => setCustomizing(true)}
              className={`${btnClass} bg-transparent border border-white/25 text-white/80 hover:text-white hover:border-white/50 flex-1 sm:flex-none`}
            >
              {t('consent.customize')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
